import { useEffect, useRef, useState } from 'react';
import type { StreamBounds } from '../../../features/quickstart/types/theme';
import { Z_ANIMATIONS } from '../../../features/quickstart/config/zIndexLayers';

interface MobileJoinIdleAnimationOverlayProps {
  animationUrl: string;
  streamBounds: StreamBounds;
  isForceRotated?: boolean;
  visible: boolean;
}

const hasNonZeroBounds = (bounds: StreamBounds): boolean =>
  bounds.width > 0 && bounds.height > 0;

const isVideoUrl = (url: string): boolean =>
  /\.(mp4|webm)(\?|$)/i.test(url);

/**
 * Mobile join/idle animation overlay — plays an animated webp/avif/mp4 over the stream
 * after the user enters fullscreen (join animation) and again when the player
 * goes idle/AFK in-game. Purely visual; all input passes through to the
 * gamepad and stream beneath.
 *
 * Architecture matches SeamlessBackground: the component is ALWAYS in the DOM
 * and visibility is controlled via the `visible` prop. The video is never
 * unmounted, eliminating the iOS race where a freshly-mounted video flashes
 * native controls before JS can call .play().
 */
export const MobileJoinIdleAnimationOverlay = ({
  animationUrl,
  streamBounds,
  isForceRotated = false,
  visible,
}: MobileJoinIdleAnimationOverlayProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const previousUrl = useRef(animationUrl);
  const useBounds = hasNonZeroBounds(streamBounds);
  const isVideo = isVideoUrl(animationUrl);

  const handleVideoLoaded = () => {
    setVideoLoaded(true);
    const video = videoRef.current;
    if (video && visible) {
      video.play().catch(() => { /* autoplay blocked */ });
    }
  };

  // Reset state when animationUrl changes
  useEffect(() => {
    if (animationUrl !== previousUrl.current) {
      setVideoLoaded(false);
      previousUrl.current = animationUrl;
    }
  }, [animationUrl]);

  // Play/pause video based on visibility
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (visible) {
      video.currentTime = 0;
      video.muted = true;
      video.play().catch(() => { /* autoplay blocked until user gesture */ });
    } else {
      video.pause();
    }
  }, [visible, animationUrl]);


  const rotatedStyle: React.CSSProperties = isForceRotated
    ? {
        position: 'fixed',
        top: '50%',
        left: '50%',
        width: '100vh',
        height: '100vw',
        maxWidth: '100dvh',
        transform: 'translate(-50%, -50%) rotate(var(--force-rotate-angle, 90deg))',
        transformOrigin: 'center center',
        zIndex: Z_ANIMATIONS,
      }
    : {
        position: useBounds ? 'absolute' : 'fixed',
        zIndex: Z_ANIMATIONS,
        ...(useBounds
          ? {
              top: streamBounds.top,
              left: streamBounds.left,
              width: streamBounds.width,
              height: streamBounds.height,
            }
          : { inset: 0 }),
      };

  return (
    <div
      className="pointer-events-none"
      style={{
        ...rotatedStyle,
        opacity: visible ? 1 : 0,
        transition: 'opacity 300ms ease-in-out',
      }}
    >
      {/* Animation — height-locked, centered.
         When rotated, CSS axes swap: container width = visual height, so use w-full h-auto. */}
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
        {isVideo ? (
          <video
            ref={videoRef}
            src={animationUrl}
            autoPlay
            muted
            loop
            playsInline
            preload='auto'
            data-overlay-video
            onLoadedData={handleVideoLoaded}
            className={isForceRotated ? 'w-full h-auto max-h-none' : 'h-full w-auto max-w-none'}
            style={{
              opacity: videoLoaded ? 1 : 0,
              transition: 'opacity 200ms ease-in',
            }}
          />
        ) : (
          <img
            src={animationUrl}
            alt=""
            className={isForceRotated ? 'w-full h-auto max-h-none' : 'h-full w-auto max-w-none'}
          />
        )}
      </div>
    </div>
  );
};
